import React, { useState, useEffect } from 'react';
import { DollarSign, QrCode, Copy, RefreshCw, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import QRCode from 'qrcode';
import { supabase } from '../lib/supabaseClient';

interface NowPaymentsDepositProps {
  userId: string;
  onDepositComplete?: () => void;
  onSuccess?: () => void;
}

const NowPaymentsDeposit: React.FC<NowPaymentsDepositProps> = ({ userId, onDepositComplete, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [payCurrency, setPayCurrency] = useState('BTC');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [statusPolling, setStatusPolling] = useState<number | null>(null);

  // Available cryptocurrencies
  const availableCurrencies = [
    { symbol: 'BTC', name: 'Bitcoin' },
    { symbol: 'ETH', name: 'Ethereum' },
    { symbol: 'LTC', name: 'Litecoin' },
    { symbol: 'DOGE', name: 'Dogecoin' },
    { symbol: 'XRP', name: 'Ripple' },
    { symbol: 'BNB', name: 'Binance Coin' },
    { symbol: 'USDT', name: 'Tether' },
    { symbol: 'USDC', name: 'USD Coin' },
    { symbol: 'SOL', name: 'Solana' },
    { symbol: 'ADA', name: 'Cardano' }
  ];

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (statusPolling) {
        clearInterval(statusPolling);
      }
    };
  }, [statusPolling]);

  // Generate QR code when payment address is available
  useEffect(() => {
    if (paymentData?.pay_address) {
      generateQrCode(paymentData.pay_address);
    }
  }, [paymentData]);

  const generateQrCode = async (address: string) => {
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(address);
      setQrCodeUrl(qrCodeDataUrl);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code');
    }
  };

  const handleCreatePayment = async () => {
    setLoading(true);
    setError(null);
    setPaymentData(null);
    setPaymentStatus(null);
    
    if (statusPolling) {
      clearInterval(statusPolling);
      setStatusPolling(null);
    }

    try {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        throw new Error('Please enter a valid amount');
      }

      // Call the Edge Function to create a payment
      const supabaseFunctionsUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/nowpayments-create-payment`;
      
      const response = await fetch(supabaseFunctionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          amount: numAmount,
          pay_currency: payCurrency,
          user_id: userId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment');
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to create payment');
      }

      setPaymentData(data);
      setPaymentStatus('waiting');

      // Start polling for payment status
      const interval = setInterval(() => {
        checkPaymentStatus(data.payment_id);
      }, 15000); // Check every 15 seconds
      
      setStatusPolling(interval);
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (paymentId: string) => {
    try {
      // Call the Edge Function to check payment status
      const supabaseFunctionsUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/nowpayments-check-payment`;

      const response = await fetch(supabaseFunctionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          payment_id: paymentId,
          user_id: userId,
          amount: parseFloat(amount)
        }),
      });

      if (!response.ok) {
        console.error('Error checking payment status:', await response.text());
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        setPaymentStatus(data.payment_status);
        
        // If payment is completed, stop polling and notify parent
        if (data.payment_status === 'finished') {
          if (statusPolling) {
            clearInterval(statusPolling);
            setStatusPolling(null);
          }

          // Call both callbacks if provided
          onDepositComplete?.();
          onSuccess?.();
        }
        
        // If payment failed or expired, stop polling
        if (['failed', 'expired', 'refunded'].includes(data.payment_status)) {
          if (statusPolling) {
            clearInterval(statusPolling);
            setStatusPolling(null);
          }
        }
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };


  const getStatusDisplay = () => {
    switch (paymentStatus) {
      case 'waiting':
        return { 
          icon: <Clock size={20} className="text-amber-400" />, 
          text: 'Waiting for payment', 
          color: 'text-amber-400',
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/30'
        };
      case 'confirming':
        return { 
          icon: <Clock size={20} className="text-blue-400" />, 
          text: 'Confirming transaction', 
          color: 'text-blue-400',
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/30'
        };
      case 'confirmed':
        return { 
          icon: <Clock size={20} className="text-blue-400" />, 
          text: 'Transaction confirmed, processing', 
          color: 'text-blue-400',
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/30'
        };
      case 'sending':
        return { 
          icon: <Clock size={20} className="text-blue-400" />, 
          text: 'Processing payment', 
          color: 'text-blue-400',
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/30'
        };
      case 'partially_paid':
        return { 
          icon: <AlertTriangle size={20} className="text-amber-400" />, 
          text: 'Partially paid, please send the remaining amount', 
          color: 'text-amber-400',
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/30'
        };
      case 'finished':
        return { 
          icon: <CheckCircle size={20} className="text-emerald-400" />, 
          text: 'Payment completed successfully', 
          color: 'text-emerald-400',
          bg: 'bg-emerald-500/10',
          border: 'border-emerald-500/30'
        };
      case 'failed':
        return { 
          icon: <AlertTriangle size={20} className="text-red-400" />, 
          text: 'Payment failed', 
          color: 'text-red-400',
          bg: 'bg-red-500/10',
          border: 'border-red-500/30'
        };
      case 'expired':
        return { 
          icon: <AlertTriangle size={20} className="text-red-400" />, 
          text: 'Payment expired', 
          color: 'text-red-400',
          bg: 'bg-red-500/10',
          border: 'border-red-500/30'
        };
      default:
        return { 
          icon: <Clock size={20} className="text-slate-400" />, 
          text: 'Unknown status', 
          color: 'text-slate-400',
          bg: 'bg-slate-500/10',
          border: 'border-slate-500/30'
        };
    }
  };

  return (
    <div className="space-y-6">
      {!paymentData ? (
        <>
          <div>
            <label className="block text-sm mb-2" style={{ color: '#64748b' }}>Amount (USD)</label>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount in USD"
                className="w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all"
                style={{
                  background: 'rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  color: '#1e293b'
                }}
                min="1"
                step="1"
              />
              <DollarSign size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#94a3b8' }} />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-2" style={{ color: '#64748b' }}>Pay With</label>
            <select
              value={payCurrency}
              onChange={(e) => setPayCurrency(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all custom-select"
              style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                color: '#1e293b'
              }}
            >
              {availableCurrencies.map((currency) => (
                <option key={currency.symbol} value={currency.symbol}>
                  {currency.name} ({currency.symbol})
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="rounded-xl p-4 flex items-center gap-3" style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)'
            }}>
              <AlertTriangle size={20} className="flex-shrink-0" style={{ color: '#ef4444' }} />
              <span style={{ color: '#ef4444' }}>{error}</span>
            </div>
          )}

          <button
            onClick={handleCreatePayment}
            disabled={loading || !amount}
            className="w-full py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 text-white"
            style={{
              background: loading || !amount ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6, #a855f7)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              cursor: loading || !amount ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              'Generate Deposit Address'
            )}
          </button>
        </>
      ) : (
        <div className="space-y-6">
          {/* Status Display */}
          {paymentStatus && (
            <div className={`${getStatusDisplay().bg} ${getStatusDisplay().border} border rounded-xl p-4 flex items-center gap-3`}>
              {getStatusDisplay().icon}
              <span className={getStatusDisplay().color}>{getStatusDisplay().text}</span>
            </div>
          )}

          {/* Payment Information */}
          <div className="rounded-xl p-6 border" style={{
            background: 'rgba(0, 0, 0, 0.02)',
            border: '1px solid rgba(0, 0, 0, 0.08)'
          }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#1e293b' }}>Payment Details</h3>

            <div className="space-y-4">
              <div>
                <div className="text-sm mb-1" style={{ color: '#64748b' }}>Amount to Send</div>
                <div className="text-xl font-bold" style={{ color: '#1e293b' }}>{paymentData.pay_amount} {paymentData.pay_currency}</div>
                <div className="text-xs" style={{ color: '#64748b' }}>≈ ${amount} USD</div>
              </div>

              <div>
                <div className="text-sm mb-1" style={{ color: '#64748b' }}>Payment Address</div>
                <div className="flex items-center gap-2">
                  <div className="p-3 rounded-lg font-mono text-sm break-all flex-1" style={{
                    background: '#ffffff',
                    color: '#1e293b',
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }}>
                    {paymentData.pay_address}
                  </div>
                  <button
                    onClick={() => copyToClipboard(paymentData.pay_address)}
                    className="p-2 rounded-lg transition-colors"
                    style={{
                      background: 'rgba(0, 0, 0, 0.05)',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)'}
                  >
                    {copied ? <CheckCircle size={18} style={{ color: '#22c55e' }} /> : <Copy size={18} style={{ color: '#64748b' }} />}
                  </button>
                </div>
              </div>
              
              {/* QR Code */}
              {qrCodeUrl && (
                <div className="flex justify-center py-4">
                  <div className="bg-white p-4 rounded-lg">
                    <img src={qrCodeUrl} alt="Payment QR Code" className="w-48 h-48" />
                  </div>
                </div>
              )}
              
              <div className="text-sm text-center" style={{ color: '#64748b' }}>
                <p>Scan the QR code or copy the address to complete your payment.</p>
                <p className="mt-1">Payment ID: {paymentData.payment_id}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={() => {
                setPaymentData(null);
                setQrCodeUrl(null);
                setPaymentStatus(null);
                if (statusPolling) {
                  clearInterval(statusPolling);
                  setStatusPolling(null);
                }
              }}
              className="flex-1 py-3 rounded-xl font-medium transition-colors text-white"
              style={{
                background: 'linear-gradient(135deg, #64748b, #475569)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #475569, #334155)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #64748b, #475569)'}
            >
              Cancel
            </button>

            <button
              onClick={() => checkPaymentStatus(paymentData.payment_id)}
              className="flex-1 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-white"
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb, #1d4ed8)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6, #2563eb)'}
            >
              <RefreshCw size={18} />
              Refresh Status
            </button>
          </div>

          {/* Instructions */}
          <div className="rounded-xl p-4 text-sm" style={{
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            color: '#3b82f6'
          }}>
            <p className="font-medium mb-2">Important Information</p>
            <ul className="space-y-1 list-disc pl-5">
              <li>Send exactly {paymentData.pay_amount} {paymentData.pay_currency} to the address above.</li>
              <li>The payment will be automatically processed once confirmed on the blockchain.</li>
              <li>This may take 10-60 minutes depending on network congestion.</li>
              <li>Do not close this window until the payment is confirmed.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default NowPaymentsDeposit;